import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

// Status color mapping
const statusColors = {
  green: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Active' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', label: 'Below Target' },
  red: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: 'Inactive' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400', label: 'Deactivated' },
};

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
}

// ============ INVITE MODAL ============
function InviteModal({ onClose, onInvited }) {
  const { token } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch('/api/admin/invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email, name: name || undefined }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess(`${data.message || `Invitation sent to ${email}`}${data.tempPassword ? `\n\nTemporary password: ${data.tempPassword}` : ''}`);
      setEmail('');
      setName('');

      // Notify parent to refresh members list
      if (onInvited) onInvited();

      // Auto-close only when the member got an email; if we're showing a temp
      // password the coach must copy, leave the modal open until they close it.
      if (!data.tempPassword) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-dark-surface rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Invite New Member</h2>

        {success ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
            <p className="text-green-700 dark:text-green-300 whitespace-pre-line break-words">{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="member@example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name (optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-dark-bg text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ============ MEMBER LIST VIEW ============
function MemberList({ onSelectMember }) {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showAlerts, setShowAlerts] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/admin/members', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch members');
      const data = await res.json();
      setMembers(data.members);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedMembers = [...members].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle nulls
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    // Numeric fields
    if (['current_streak', 'weekly_xp'].includes(sortField)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }

    // String comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }

    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ field, label }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortField === field && (
          <span className="text-primary">{sortDir === 'asc' ? '▲' : '▼'}</span>
        )}
      </span>
    </th>
  );

  // Aggregate stats
  const activeMembers = members.filter(m => m.is_active);
  const avgStreak = activeMembers.length > 0
    ? (activeMembers.reduce((sum, m) => sum + m.current_streak, 0) / activeMembers.length).toFixed(1)
    : 0;
  const avgWeeklyXp = activeMembers.length > 0
    ? Math.round(activeMembers.reduce((sum, m) => sum + m.weekly_xp, 0) / activeMembers.length)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-500">Loading members...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error loading members: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header & Stats */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Team overview and member management</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium flex items-center gap-2 self-start"
          data-testid="invite-member-btn"
        >
          <span>+</span>
          <span>Invite Member</span>
        </button>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onInvited={fetchMembers}
        />
      )}

      {/* Aggregate stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
          <div className="text-sm text-gray-500">Total Members</div>
          <div className="text-2xl font-bold text-gray-800">{members.length}</div>
          <div className="text-xs text-gray-400">{activeMembers.length} active</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
          <div className="text-sm text-gray-500">Avg Streak</div>
          <div className="text-2xl font-bold text-orange-600">{avgStreak} days</div>
          <div className="text-xs text-gray-400">across active members</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
          <div className="text-sm text-gray-500">Avg Weekly XP</div>
          <div className="text-2xl font-bold text-blue-600">{avgWeeklyXp} XP</div>
          <div className="text-xs text-gray-400">this week</div>
        </div>
      </div>

      {/* Alerts Panel */}
      {(() => {
        const inactiveMembers = members.filter(m => m.status === 'red' && m.is_active);
        const belowTargetMembers = members.filter(m => m.status === 'yellow' && m.is_active);
        const totalAlerts = inactiveMembers.length + belowTargetMembers.length;

        if (totalAlerts === 0) return null;

        return (
          <div className="mb-6" data-testid="alerts-panel">
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className="w-full flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">⚠️</span>
                <div className="text-left">
                  <div className="font-semibold text-amber-800">
                    {totalAlerts} Alert{totalAlerts !== 1 ? 's' : ''} Need Attention
                  </div>
                  <div className="text-sm text-amber-600">
                    {inactiveMembers.length > 0 && `${inactiveMembers.length} inactive (3+ days)`}
                    {inactiveMembers.length > 0 && belowTargetMembers.length > 0 && ' • '}
                    {belowTargetMembers.length > 0 && `${belowTargetMembers.length} below target`}
                  </div>
                </div>
              </div>
              <span className="text-amber-600">{showAlerts ? '▲' : '▼'}</span>
            </button>

            {showAlerts && (
              <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow overflow-hidden">
                {/* Inactive Members Alert */}
                {inactiveMembers.length > 0 && (
                  <div className="border-b border-gray-100">
                    <div className="px-4 py-2 bg-red-50 border-b border-red-100">
                      <span className="text-sm font-medium text-red-700">
                        🔴 Inactive Members (3+ days)
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {inactiveMembers.map(member => {
                        const daysInactive = member.last_active_at
                          ? Math.floor((Date.now() - new Date(member.last_active_at)) / (1000 * 60 * 60 * 24))
                          : 'N/A';
                        return (
                          <div
                            key={member.id}
                            className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                            onClick={() => onSelectMember(member.id)}
                            data-testid={`alert-inactive-${member.id}`}
                          >
                            <div>
                              <div className="font-medium text-gray-800">{member.name}</div>
                              <div className="text-sm text-gray-500">{member.email}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium text-red-600" data-testid="days-inactive">
                                {daysInactive} day{daysInactive !== 1 ? 's' : ''} inactive
                              </div>
                              <div className="text-xs text-gray-400">Last: {daysAgo(member.last_active_at)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Below Target Members Alert */}
                {belowTargetMembers.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-100">
                      <span className="text-sm font-medium text-yellow-700">
                        🟡 Below Target (no streak)
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                      {belowTargetMembers.slice(0, 5).map(member => (
                        <div
                          key={member.id}
                          className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                          onClick={() => onSelectMember(member.id)}
                        >
                          <div>
                            <div className="font-medium text-gray-800">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-yellow-600">
                              {member.weekly_xp} XP this week
                            </div>
                            <div className="text-xs text-gray-400">{daysAgo(member.last_active_at)}</div>
                          </div>
                        </div>
                      ))}
                      {belowTargetMembers.length > 5 && (
                        <div className="px-4 py-2 text-sm text-gray-500 text-center">
                          +{belowTargetMembers.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Members table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" data-testid="members-table">
            <thead className="bg-gray-50">
              <tr>
                <SortHeader field="name" label="Name" />
                <SortHeader field="current_streak" label="Streak" />
                <SortHeader field="weekly_xp" label="Weekly XP" />
                <SortHeader field="last_active_at" label="Last Active" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedMembers.map(member => {
                const sc = statusColors[member.status] || statusColors.green;
                return (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{member.name}</div>
                          <div className="text-xs text-gray-500">{member.email}</div>
                          {member.role === 'coach' && (
                            <span className="inline-block mt-0.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Coach</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-800">{member.current_streak}</span>
                      <span className="text-xs text-gray-400 ml-1">days</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-800">{member.weekly_xp}</span>
                      <span className="text-xs text-gray-400 ml-1">XP</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {daysAgo(member.last_active_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                        <span className={`w-2 h-2 rounded-full ${sc.dot}`}></span>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onSelectMember(member.id)}
                        className="text-sm text-primary hover:text-primary/80 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sortedMembers.length === 0 && (
          <div className="text-center py-8 text-gray-500">No members found</div>
        )}
      </div>
    </div>
  );
}

// ============ MEMBER DETAIL VIEW ============
function MemberDetail({ memberId, onBack }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const fetchMember = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/admin/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch member details');
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [memberId, token]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  const handleDeactivate = async () => {
    try {
      setDeactivating(true);
      const res = await fetch(`/api/admin/members/${memberId}/deactivate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to deactivate');
      }
      setShowConfirm(false);
      // Refresh member data
      await fetchMember();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    try {
      const res = await fetch(`/api/admin/members/${memberId}/reactivate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to reactivate');
      await fetchMember();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-500">Loading member details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button onClick={onBack} className="text-primary hover:underline mb-4 flex items-center gap-1">
          &larr; Back to Members
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      </div>
    );
  }

  const { member, dailyLogs, dailyScores, weeklyScores, monthlyCompletions, rocks } = data;

  // Group daily logs by date
  const logsByDate = {};
  dailyLogs.forEach(log => {
    if (!logsByDate[log.log_date]) logsByDate[log.log_date] = [];
    logsByDate[log.log_date].push(log);
  });
  const sortedDates = Object.keys(logsByDate).sort((a, b) => b.localeCompare(a));

  const categoryColors = {
    database: 'text-green-700 bg-green-50',
    grassroots: 'text-yellow-700 bg-yellow-50',
    strategic_partners: 'text-blue-700 bg-blue-50',
  };

  return (
    <div>
      {/* Back button & header */}
      <button onClick={onBack} className="text-primary hover:underline mb-4 flex items-center gap-1 text-sm">
        &larr; Back to Members
      </button>

      {/* Member header card */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-100 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{member.name}</h2>
            <p className="text-gray-500">{member.email}</p>
            <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
              <span>Joined {formatDate(member.created_at)}</span>
              <span>|</span>
              <span>Last active {daysAgo(member.last_active_at)}</span>
              {!member.is_active && (
                <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">Deactivated</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {member.is_active ? (
              <>
                {!showConfirm ? (
                  <button
                    onClick={() => setShowConfirm(true)}
                    className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium border border-red-200"
                  >
                    Deactivate Account
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600">Are you sure?</span>
                    <button
                      onClick={handleDeactivate}
                      disabled={deactivating}
                      className="px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                      {deactivating ? 'Deactivating...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-sm hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={handleReactivate}
                className="px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 text-sm font-medium border border-green-200"
              >
                Reactivate Account
              </button>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Current Streak</div>
            <div className="text-xl font-bold text-orange-600">{member.current_streak} days</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Best Streak</div>
            <div className="text-xl font-bold text-purple-600">{member.best_streak} days</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Total XP</div>
            <div className="text-xl font-bold text-blue-600">{member.total_xp}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500">Role</div>
            <div className="text-xl font-bold text-gray-800 capitalize">{member.role}</div>
          </div>
        </div>
      </div>

      {/* Daily Logs Section */}
      <div className="bg-white rounded-lg shadow border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Daily Habit Logs</h3>
        </div>
        <div className="p-6">
          {sortedDates.length === 0 ? (
            <p className="text-gray-500 text-sm">No daily logs recorded yet.</p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {sortedDates.slice(0, 14).map(date => (
                <div key={date} className="border border-gray-100 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-700 mb-2">{formatDate(date)}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {logsByDate[date].map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${categoryColors[log.category] || 'text-gray-600 bg-gray-50'}`}>
                            {log.category.replace('_', ' ')}
                          </span>
                          <span className="text-gray-700">{log.habit_name}</span>
                        </span>
                        <span className="font-medium text-gray-800">
                          {log.tally_count > 0 ? `${log.tally_count}x` : log.completed ? '✓' : '—'}
                          <span className="text-xs text-gray-400 ml-1">{log.xp_earned} XP</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Weekly Scores Section */}
      <div className="bg-white rounded-lg shadow border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Weekly Scores</h3>
        </div>
        <div className="p-6">
          {weeklyScores.length === 0 ? (
            <p className="text-gray-500 text-sm">No weekly scores recorded yet.</p>
          ) : (
            <div className="space-y-3">
              {weeklyScores.map((week, idx) => (
                <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <span className="text-sm font-medium text-gray-700">{week.week_label}</span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-500">{week.days_tracked} days</span>
                    <span className="font-bold text-blue-600">{week.weekly_xp} XP</span>
                    {week.weekly_max_xp > 0 && (
                      <span className="text-gray-400">/ {week.weekly_max_xp}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Completions Section */}
      <div className="bg-white rounded-lg shadow border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Monthly Completions</h3>
        </div>
        <div className="p-6">
          {monthlyCompletions.length === 0 ? (
            <p className="text-gray-500 text-sm">No monthly completions recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {monthlyCompletions.map((mc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-700">{mc.habit_name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">{formatDate(mc.log_date)}</span>
                    <span className={mc.completed ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {mc.completed ? '✓ Completed' : 'Not completed'}
                    </span>
                    <span className="text-blue-600 font-medium">{mc.xp_earned} XP</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quarterly Rocks Section */}
      <div className="bg-white rounded-lg shadow border border-gray-100 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Quarterly Rocks</h3>
        </div>
        <div className="p-6">
          {rocks.length === 0 ? (
            <p className="text-gray-500 text-sm">No quarterly rocks set yet.</p>
          ) : (
            <div className="space-y-4">
              {rocks.map(rock => (
                <div key={rock.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-gray-800">{rock.name}</h4>
                      <span className="text-xs text-gray-500 capitalize">{rock.category.replace(/_/g, ' ')} | {rock.quarter_label}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${rock.is_completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {rock.is_completed ? 'Completed' : 'In Progress'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    Due: {formatDate(rock.due_date)} | Check-ins: {rock.checkin_count}
                  </div>
                  {rock.checkins && rock.checkins.length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <div className="text-xs font-medium text-gray-500 mb-2">Recent Check-ins:</div>
                      <div className="space-y-1">
                        {rock.checkins.slice(0, 4).map((ci, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400">{formatDate(ci.checkin_date)}</span>
                            <span className={`px-1.5 py-0.5 rounded ${
                              ci.status === 'on_track' ? 'bg-green-100 text-green-700' :
                              ci.status === 'off_track' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {ci.status.replace('_', ' ')}
                            </span>
                            {ci.action_taken && <span className="text-gray-600 truncate">{ci.action_taken}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ MAIN ADMIN PAGE ============
export default function AdminPage() {
  const { user } = useAuth();
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  // Only coaches can access admin
  if (user?.role !== 'coach') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      {selectedMemberId ? (
        <MemberDetail
          memberId={selectedMemberId}
          onBack={() => setSelectedMemberId(null)}
        />
      ) : (
        <MemberList onSelectMember={setSelectedMemberId} />
      )}
    </div>
  );
}
